import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { renamePortfolio } from "../../../shared/api";

const PROCESSING_STATUSES = new Set(["queued", "processing"]);
const DISABLED_STATUSES = new Set(["queued", "processing", "failed"]);

const getStatusColor = (status) => {
  if (status === "completed") return "success.main";
  if (status === "failed") return "error.main";
  return "warning.main";
};

export default function PortfolioSelectorDialog({
  selectedPortfolioId,
  onPortfolioChange,
  configData,
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [nextName, setNextName] = useState("");
  const [renameErrorMessage, setRenameErrorMessage] = useState("");

  const portfolios = configData?.portfolios?.available ?? [];
  const selectedPortfolio = portfolios.find(
    (portfolio) => portfolio.id === selectedPortfolioId,
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language || "en", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [i18n.language],
  );

  const renameMutation = useMutation({
    mutationFn: ({ portfolioId, name }) =>
      renamePortfolio({
        clientId: configData?.clientId,
        portfolioId,
        name,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["config", configData?.clientId],
      });
      setEditingPortfolioId(null);
      setNextName("");
    },
  });

  const handleOpen = () => {
    if (configData?.clientId) {
      queryClient.refetchQueries({
        queryKey: ["config", configData.clientId],
      });
    }
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
    setEditingPortfolioId(null);
    setNextName("");
    setRenameErrorMessage("");
  };

  const handleSelectPortfolio = (portfolio) => {
    if (DISABLED_STATUSES.has(portfolio.status)) {
      return;
    }

    onPortfolioChange?.(portfolio.id);
    setIsOpen(false);
  };

  const handleStartEditing = (portfolio) => {
    setEditingPortfolioId(portfolio.id);
    setNextName(portfolio.name || "");
    setRenameErrorMessage("");
  };

  const handleCancelEditing = () => {
    setEditingPortfolioId(null);
    setNextName("");
    setRenameErrorMessage("");
  };

  const handleSaveEditing = async (portfolioId) => {
    const trimmedName = nextName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      await renameMutation.mutateAsync({ portfolioId, name: trimmedName });
    } catch (error) {
      console.error("Failed to rename portfolio:", error);
      setRenameErrorMessage(
        error?.error ||
          error?.message ||
          t("portfolioMap.portfolioSelector.renameFailed"),
      );
    }
  };

  const getCreatedAtLabel = (createdAt) => {
    if (!createdAt) return t("portfolioMap.portfolioSelector.unknownDate");
    const parsedDate = new Date(createdAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return t("portfolioMap.portfolioSelector.unknownDate");
    }

    return dateFormatter.format(parsedDate);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 10,
        right: 50,
        zIndex: 1000,
      }}
    >
      <Paper elevation={3} sx={{ borderRadius: "4px" }}>
        <Button
          onClick={handleOpen}
          startIcon={<WorkspacesOutlinedIcon />}
          sx={{
            px: 1.5,
            py: 0.75,
            textTransform: "none",
            minWidth: 200,
            justifyContent: "flex-start",
          }}
        >
          <Typography variant="body2" noWrap>
            {selectedPortfolio?.name ||
              t("portfolioMap.portfolioSelector.noPortfolioFound")}
          </Typography>
        </Button>
      </Paper>

      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {t("portfolioMap.portfolioSelector.dialogTitle")}
          <IconButton
            aria-label={t("common.close", { defaultValue: "Close" })}
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {portfolios.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("portfolioMap.noPortfolioFound", {
                  defaultValue: "No portfolio found for this client yet.",
                })}
              </Typography>
            )}

            {portfolios.map((portfolio) => {
              const isDisabled = DISABLED_STATUSES.has(portfolio.status);
              const isProcessing = PROCESSING_STATUSES.has(portfolio.status);
              const isEditing = editingPortfolioId === portfolio.id;
              const isSelected = selectedPortfolioId === portfolio.id;

              return (
                <Paper
                  key={portfolio.id}
                  variant="outlined"
                  onClick={() => handleSelectPortfolio(portfolio)}
                  sx={{
                    p: 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.6 : 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box flex={1} minWidth={0}>
                      {isEditing ? (
                        <>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <TextField
                              size="small"
                              fullWidth
                              value={nextName}
                              onChange={(e) => setNextName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={renameMutation.isPending}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEditing(portfolio.id);
                              }}
                              disabled={
                                !nextName.trim() || renameMutation.isPending
                              }
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEditing();
                              }}
                              disabled={renameMutation.isPending}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          {renameErrorMessage && (
                            <Typography variant="body2" color="error">
                              {renameErrorMessage}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {portfolio.name}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t("portfolioMap.portfolioSelector.createdAt", {
                          date: getCreatedAtLabel(portfolio.createdAt),
                        })}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {isProcessing && <CircularProgress size={14} />}
                      <Typography
                        variant="caption"
                        color={getStatusColor(portfolio.status)}
                        sx={{ textTransform: "capitalize" }}
                      >
                        {t(
                          `portfolioMap.portfolioSelector.status.${portfolio.status}`,
                          { defaultValue: portfolio.status },
                        )}
                      </Typography>
                      {!isEditing && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEditing(portfolio);
                          }}
                          disabled={renameMutation.isPending}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
